import React, { useMemo, useState } from 'react';
import { Avatar, Button, Icon, ScoreChip, ResultStatus, useToast } from '../../components/ui';
import { batchAPI } from '../../services/api';
import { resultDateText, inviteLinkOf, hasReport } from './derive';
import { copyText, sendSingleInvite } from './actions';
import ReportModal from './ReportModal';
import RecordingModal from './RecordingModal';

/** Rows that can still get an invite: not started yet (pending or invited). */
const invitable = (r) => {
  const st = String(r.row.invite_status || 'pending').toLowerCase();
  return !String(r.row.id).startsWith('synced-') && (st === 'pending' || st === 'invited') && r.state === 'invited';
};

/**
 * The candidates of one batch, opened from a batch row in the Interviews tab:
 * pick people (or all not-started) and send their invites in one go. Sending
 * always asks first, says how many emails go out, and goes through the batch
 * invite endpoint, which only touches rows of this batch (tenancy is checked
 * server-side).
 */
export default function BatchCandidates({ campaign, examSet, batch, data, closed }) {
  const toast = useToast();
  const entries = useMemo(
    () => data.people
      .filter((p) => p.results[examSet.id] && p.results[examSet.id].batchId === batch.id)
      .map((p) => ({ p, r: p.results[examSet.id] }))
      .sort((a, b) => a.p.name.localeCompare(b.p.name)),
    [data.people, examSet.id, batch.id],
  );
  const eligible = entries.filter(({ r }) => invitable(r)).map(({ r }) => r.row.id);
  const [picked, setPicked] = useState(() => new Set());
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [report, setReport] = useState(null);
  const [recording, setRecording] = useState(null);
  const allPicked = eligible.length > 0 && eligible.every((id) => picked.has(id));
  const notInvited = entries.filter(({ r }) => invitable(r) && String(r.row.invite_status || 'pending').toLowerCase() === 'pending').length;

  const toggle = (id) => setPicked((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const sendPicked = async () => {
    setSending(true);
    try {
      const res = await batchAPI.invite(campaign.id, examSet.id, batch.id, [...picked]);
      const { sent_count: sent = 0, skipped = [], failed = [] } = res.data || {};
      const extra = [skipped.length && `${skipped.length} skipped`, failed.length && `${failed.length} failed`].filter(Boolean).join(', ');
      toast(`${sent} invite${sent === 1 ? '' : 's'} sent${extra ? ` · ${extra}` : ''}`);
      setPicked(new Set());
      setConfirming(false);
      await data.reloadAssignments();
    } catch (err) {
      toast(err.response?.data?.detail || 'Failed to send invites.');
    } finally {
      setSending(false);
    }
  };

  const sendOne = async (row) => {
    setBusyId(row.id);
    try {
      await sendSingleInvite(row.id);
      toast(String(row.invite_status || 'pending').toLowerCase() === 'pending' ? 'Invite sent.' : 'Invite resent.');
      await data.reloadAssignments();
    } catch (err) {
      toast(err.message || 'Failed to send invite.');
    } finally {
      setBusyId(null);
    }
  };

  if (!entries.length) {
    return (
      <div className="wz-bcands">
        <p className="wz-sub" style={{ padding: '12px 0' }}>
          {closed
            ? `No candidates in ${batch.name}. This batch is closed, so no one can be added or invited.`
            : `No candidates in ${batch.name} yet. Use “Add” to put people in it.`}
        </p>
      </div>
    );
  }

  return (
    <div className="wz-bcands" role="region" aria-label={`Candidates in ${batch.name}`}>
      <div className="wz-bcands-bar">
        {confirming ? (
          <span className="wz-confirm-inline" role="alertdialog" aria-label="Send invites now?">
            <span className="wz-confirm-ic" aria-hidden="true"><Icon name="mail" /></span>
            <span><b>Send {picked.size} invite{picked.size === 1 ? '' : 's'} now?</b> <span className="wz-sub">Each person gets an email with their exam link{batch.closes_at ? ', valid until the batch closes' : ''}.</span></span>
            <span style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <Button small onClick={() => setConfirming(false)} disabled={sending}>Cancel</Button>
              <Button small variant="primary" icon="mail" autoFocus disabled={sending} onClick={sendPicked}>
                {sending ? 'Sending…' : `Yes, send ${picked.size}`}
              </Button>
            </span>
          </span>
        ) : (
          <>
            <label className="wz-check">
              <input type="checkbox" disabled={!eligible.length || closed} checked={allPicked} onChange={() => setPicked(allPicked ? new Set() : new Set(eligible))} />
              {picked.size ? `${picked.size} selected` : 'Select all not started'}
            </label>
            <span className="wz-sub">
              {entries.length} candidate{entries.length === 1 ? '' : 's'} · {notInvited} not invited yet
              {closed ? ' · batch is closed, invites can’t be sent' : ''}
            </span>
            <Button small variant="primary" icon="mail" style={{ marginLeft: 'auto' }} disabled={!picked.size || closed} onClick={() => setConfirming(true)}>
              Send invites{picked.size ? ` (${picked.size})` : ''}
            </Button>
          </>
        )}
      </div>
      {entries.map(({ p, r }) => {
        const can = invitable(r) && !closed;
        const pending = String(r.row.invite_status || 'pending').toLowerCase() === 'pending';
        const link = inviteLinkOf(r.row);
        return (
          <div className="wz-bcand" key={r.row.id}>
            <span style={{ width: 16, display: 'inline-flex' }}>
              {invitable(r) && <input type="checkbox" aria-label={`Select ${p.name}`} disabled={closed} checked={picked.has(r.row.id)} onChange={() => toggle(r.row.id)} />}
            </span>
            <Avatar name={p.name} size={30} />
            <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span style={{ fontWeight: 500 }} className="wz-ellipsis">{p.name}</span>
              <span className="wz-sub wz-ellipsis" style={{ fontSize: 12 }}>{p.email}</span>
            </span>
            <ResultStatus state={r.state} />
            <span className="wz-sub" style={{ fontSize: 13 }}>{resultDateText(r)}</span>
            {r.state === 'done' ? <ScoreChip result={r} /> : <span />}
            <span className="acts">
              {hasReport(r.session) && (
                <button type="button" className="wz-linkbtn" onClick={() => setReport(r.session.id)}>Report</button>
              )}
              {r.session?.has_recording && (
                <button type="button" className="wz-linkbtn" title="Recording with proctoring flags"
                  onClick={() => setRecording({ sessionId: r.session.id, name: p.name, subtitle: [examSet.name, batch.name, resultDateText(r)].filter(Boolean).join(' · ') })}>Recording</button>
              )}
              <button type="button" className="wz-linkbtn" disabled={!link} onClick={async () => { if (link && await copyText(link)) toast('Exam link copied'); }}>Copy link</button>
              {can && (
                <button type="button" className="wz-linkbtn" disabled={busyId === r.row.id} onClick={() => sendOne(r.row)}>
                  {busyId === r.row.id ? '…' : pending ? 'Send invite' : 'Resend'}
                </button>
              )}
            </span>
          </div>
        );
      })}
      {report && <ReportModal sessionId={report} onClose={() => setReport(null)} />}
      {recording && <RecordingModal {...recording} onClose={() => setRecording(null)} />}
    </div>
  );
}
